import { useEffect, useMemo, useState } from 'react'
import { ArrowDown, ArrowUp, Download, Loader2 } from 'lucide-react'
import type { BatteryOverheatReportRow } from './types'
import { downloadBatteryOverheatCsv, fetchBatteryOverheatReport } from './useAdminApi'
import { AdminTable, Banner, Field, SectionHeader, Td, Th, inputCls } from './ui'

const SITE_OPTIONS: { key: string; label: string }[] = [
  { key: '', label: 'All sites' },
  { key: 'mbmt', label: 'MBMT' },
  { key: 'nagpur', label: 'Nagpur' },
  { key: 'gti', label: 'GTI (Ports)' },
  { key: 'jm_baxi', label: 'JM Baxi (Ports)' },
]

const SITE_LABEL: Record<string, string> = Object.fromEntries(SITE_OPTIONS.map((o) => [o.key, o.label]))

const SORT_COLUMNS: { key: string; label: string }[] = [
  { key: 'started_ts', label: 'Started' },
  { key: 'ended_ts', label: 'Ended' },
  { key: 'duration_sec', label: 'Duration' },
  { key: 'max_temp_c', label: 'Max temp (C)' },
  { key: 'vehicle_id', label: 'Vehicle' },
  { key: 'site', label: 'Site' },
]

const PAGE_SIZE = 25

function fmtDateTime(ms: number): string {
  return new Date(ms).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', hour12: false })
}

// <input type="datetime-local"> yields a bare "2026-10-21T13:30", no timezone -- the
// console's own convention everywhere else is IST, so this stamps +05:30 explicitly
// before it ever reaches the API. Without it, Python's datetime.fromisoformat
// (serving/app.py) would parse a timezone-naive value and Postgres would interpret it
// in the SESSION's own timezone (not IST) when casting to timestamptz -- silently
// shifting every filter by the server/session offset.
function toIstIso(v: string): string | undefined {
  return v ? `${v}:00+05:30` : undefined
}

/** Super-admin only -- enforced server-side too (403 on every /api/reports/battery-
 *  overheat* route for anyone else, same gate DarReportSection's own routes use): this
 *  spans three structurally different per-site alert sources (MBMT, Nagpur, EIM's two
 *  port sites), so rather than trying to intersect three unrelated per-site grant
 *  schemes into one check, it follows DAR's own precedent -- the only other cross-site
 *  report in this console -- and gates on "is this person the super admin" alone.
 *
 *  serving/battery_overheat_report.py's own module docstring has the full mapping of
 *  where each site's data actually lives and what MBMT/Nagpur's own "duration" means
 *  (they have no true episode-close event, unlike EIM's SQL-derived episodes -- see
 *  that docstring, not repeated here). */
export function BatteryOverheatReportSection({ isSuperAdmin }: { isSuperAdmin: boolean }): JSX.Element {
  const [site, setSite] = useState('')
  const [vehicleQuery, setVehicleQuery] = useState('')
  // Debounced -- without this, every keystroke in "Vehicle number" re-fired the fetch
  // effect below (adversarial review, MED), each request racing the next and each one
  // scanning up to _FETCH_CAP rows per site server-side for what was, until the last
  // keystroke, a throwaway intermediate string.
  const [vehicleQueryDebounced, setVehicleQueryDebounced] = useState('')
  useEffect(() => {
    const id = window.setTimeout(() => setVehicleQueryDebounced(vehicleQuery), 350)
    return () => window.clearTimeout(id)
  }, [vehicleQuery])
  const [fromTs, setFromTs] = useState('')
  const [toTs, setToTs] = useState('')
  const [sort, setSort] = useState('started_ts')
  const [dir, setDir] = useState<'asc' | 'desc'>('desc')
  const [offset, setOffset] = useState(0)

  const [rows, setRows] = useState<BatteryOverheatReportRow[]>([])
  const [total, setTotal] = useState(0)
  const [truncated, setTruncated] = useState(false)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [downloading, setDownloading] = useState(false)
  const [downloadError, setDownloadError] = useState<string | null>(null)
  const [downloadTruncated, setDownloadTruncated] = useState(false)

  // Any filter change resets paging -- holding offset across a filter change would
  // land the operator on a page number that may not exist for the new filter, same
  // reasoning every other paginated episode list in this console applies.
  useEffect(() => { setOffset(0) }, [site, vehicleQueryDebounced, fromTs, toTs, sort, dir])

  const filters = useMemo(() => ({
    site: site || undefined,
    vehicleId: vehicleQueryDebounced.trim() || undefined,
    fromTs: toIstIso(fromTs),
    toTs: toIstIso(toTs),
    sort, dir, limit: PAGE_SIZE, offset,
  }), [site, vehicleQueryDebounced, fromTs, toTs, sort, dir, offset])

  useEffect(() => {
    if (!isSuperAdmin) return
    let live = true
    setLoading(true)
    setLoadError(null)
    fetchBatteryOverheatReport(filters)
      .then((r) => { if (live) { setRows(r.rows); setTotal(r.total); setTruncated(r.truncated) } })
      .catch((e: Error) => { if (live) setLoadError(e.message) })
      .finally(() => { if (live) setLoading(false) })
    return () => { live = false }
  }, [isSuperAdmin, filters])

  async function onDownload(): Promise<void> {
    setDownloading(true)
    setDownloadError(null)
    try {
      setDownloadTruncated(await downloadBatteryOverheatCsv(filters))
    } catch (e) {
      setDownloadError(e instanceof Error ? e.message : 'Could not generate the CSV')
    } finally {
      setDownloading(false)
    }
  }

  function onSort(col: string): void {
    if (col === sort) { setDir((d) => (d === 'asc' ? 'desc' : 'asc')); return }
    setSort(col)
    setDir('desc')
  }

  if (!isSuperAdmin) {
    return <Banner tone="error">This section is limited to the super administrator.</Banner>
  }

  const hasNext = offset + rows.length < total

  return (
    <div className="flex flex-col gap-5">
      <SectionHeader
        title="Battery-overheat (temperature) report"
        subtitle="MBMT, Nagpur, GTI, JM Baxi -- every sustained BATTERY_OVERHEAT episode/event, one table."
        action={(
          <button
            type="button"
            onClick={() => void onDownload()}
            disabled={downloading}
            className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-3.5 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-60"
          >
            {downloading ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />}
            Export CSV
          </button>
        )}
      />

      {downloadError ? <Banner tone="error" onDismiss={() => setDownloadError(null)}>{downloadError}</Banner> : null}
      {loadError ? <Banner tone="error" onDismiss={() => setLoadError(null)}>{loadError}</Banner> : null}
      {truncated ? (
        <Banner tone="error">
          This result hit the report's row cap and may be missing matches -- narrow the date range or vehicle
          number for a complete count.
        </Banner>
      ) : null}
      {downloadTruncated ? (
        <Banner tone="error" onDismiss={() => setDownloadTruncated(false)}>
          The downloaded CSV hit the report's row cap and may be missing rows -- narrow the date range or
          vehicle number and export again.
        </Banner>
      ) : null}

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Field label="Site">
          <select value={site} onChange={(e) => setSite(e.target.value)} className={inputCls}>
            {SITE_OPTIONS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
          </select>
        </Field>
        <Field label="Vehicle number">
          <input
            type="text" value={vehicleQuery} onChange={(e) => setVehicleQuery(e.target.value)}
            placeholder="Contains…" className={inputCls}
          />
        </Field>
        <Field label="From">
          <input type="datetime-local" value={fromTs} onChange={(e) => setFromTs(e.target.value)} className={inputCls} />
        </Field>
        <Field label="To">
          <input type="datetime-local" value={toTs} onChange={(e) => setToTs(e.target.value)} className={inputCls} />
        </Field>
      </div>

      {loading ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-400">
          No battery-overheat episodes match this filter.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <AdminTable>
            <thead>
              <tr>
                {SORT_COLUMNS.map((c) => (
                  <Th key={c.key} className={c.key === 'max_temp_c' || c.key === 'duration_sec' ? 'text-right' : undefined}>
                    <button
                      type="button"
                      onClick={() => onSort(c.key)}
                      className="inline-flex items-center gap-1 hover:text-slate-900"
                    >
                      {c.label}
                      {sort === c.key ? (dir === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />) : null}
                    </button>
                  </Th>
                ))}
                <Th>Vehicle state</Th>
                <Th>Guns</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <Td mono>{fmtDateTime(r.started_ts)}</Td>
                  <Td mono>{fmtDateTime(r.ended_ts)}</Td>
                  <Td mono className="text-right">{Math.round(r.duration_sec / 60)}m</Td>
                  <Td mono className="text-right">{r.max_temp_c.toFixed(1)}</Td>
                  <Td mono>{r.vehicle_id}</Td>
                  <Td>{SITE_LABEL[r.site] ?? r.site}</Td>
                  <Td>{r.vehicle_status ?? '—'}</Td>
                  <Td mono>{r.charging_guns ?? '—'}</Td>
                </tr>
              ))}
            </tbody>
          </AdminTable>
        </div>
      )}

      {(offset > 0 || hasNext) && (
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={offset === 0}
            onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
            className="rounded-lg px-2.5 py-1 text-xs font-medium text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Previous
          </button>
          <button
            type="button"
            disabled={!hasNext}
            onClick={() => setOffset((o) => o + PAGE_SIZE)}
            className="rounded-lg px-2.5 py-1 text-xs font-medium text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Next
          </button>
          <span className="ml-auto font-mono text-xs text-slate-400">
            {total === 0 ? '—' : `${offset + 1}–${offset + rows.length} of ${total}`}
          </span>
        </div>
      )}
    </div>
  )
}
